import { Device } from '@capacitor/device';
import { Roles } from '@dataquiver/stoneage-roles';
import { IonButton, IonIcon, IonItem, IonNote, IonRow, IonToggle } from '@ionic/react';
import { pencil, personCircle } from 'ionicons/icons/';
import React, { FC, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { ProfileMessages } from 'src/intl/ProfileMessages';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { fetchUserInfo } from 'src/redux/auth/thunk/fetchUserInfo';
import { profilePreferenceActions } from 'src/redux/profile-preferences/actions';
import { selectAppTheme, selectIsMetric } from 'src/redux/profile-preferences/selectors';
import { AppState } from 'src/redux/types';
import { useSocket, useSocketGet } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { SupportedLanguages } from '../../intl/SupportedLanguages';
import { DqText } from '../dq-text';
import EditProfileModal, { EditProfileState } from './EditProfileModal';
import FeedbackModal from './FeedbackModal';
import './ProfileDetails.scss';
import { GenericMessages } from '../../intl/GenericMessages';
import { useToast } from '../reusable-components/useToast';

export const MODE = {
  DARK: 'dark',
  LIGHT: 'light',
};

interface ProfileDetailsProps {
  buildInfo?: { version: string; build: string };
}

export const ProfileDetails: FC<ProfileDetailsProps> = props => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const userData = useSelector(selectAuthUserInfo);
  const theme = useSelector(selectAppTheme);
  const isMetric = useSelector(selectIsMetric);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState<boolean>(false);
  const { request: sentinelRequest } = useSocket(SocketContextSentinel, 'faqs');
  const { item: organization } = useSocketGet(SocketContextPortal, 'organizations', userData?.org);
  const [editProfilekModalOpen, setEditProfileModalOpen] = React.useState(false);
  const [languageDisplay, setLanguageDisplay] = React.useState<string>('');

  const { update: updateUser } = useSocket(SocketContextPortal, 'users');
  const [present] = useToast();

  const profileDetailsRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    profileDetailsRef.current?.scrollIntoView();
  };

  const submitFeedback = useCallback(
    async (feedbackText: string | undefined, canContact: boolean) => {
      setFeedbackModalOpen(false);
      scrollToTop();
      await sentinelRequest('feedback sendFeedback', { feedback: feedbackText, canContact: canContact })
        .then(() => {
          present(
            {
              message: formatMessage(ProfileMessages.feedbackSuccess),
            },
            'success',
          );
        })
        .catch(e => {
          console.log('error sending message', e);
        });
    },
    [formatMessage, present, sentinelRequest],
  );

  const toggleDarkMode = useCallback(
    (toggleSwitch: boolean) => {
      if (toggleSwitch) {
        return dispatch(profilePreferenceActions.setTheme(MODE.DARK));
      }
      return dispatch(profilePreferenceActions.setTheme(MODE.LIGHT));
    },
    [dispatch],
  );

  const cancelUpdate = useCallback(() => {
    setEditProfileModalOpen(false);
  }, []);

  const updateProfile = async (_id: string | undefined, updating: EditProfileState) => {
    try {
      if (!_id) throw new Error("Can't update a user without an id!");
      await updateUser({
        _id,
        update: {
          $set: { family_name: updating.familyName, given_name: updating.givenName },
        },
      });
      dispatch(fetchUserInfo());
      present(
        {
          message: formatMessage(ProfileMessages.profileNameUpdated),
        },
        'success',
      );
      setEditProfileModalOpen(false);
    } catch (e) {
      console.error('Failed to perform update:', e); // todo: error banner?
    }
  };

  React.useEffect(() => {
    Device.getLanguageCode().then(languageCode => {
      const lang = SupportedLanguages.find(lang => lang === languageCode.value) ?? 'en';
      const languageDisplayNames = new Intl.DisplayNames([lang], { type: 'language' });
      const languageDisplayName = languageDisplayNames.of(lang)!;
      setLanguageDisplay(languageDisplayName);
    });
  }, [setLanguageDisplay]);

  const openSentinelManual = () => {
    history.push(`/pdf-viewer/${encodeURIComponent('/assets/Sentinel_3L_System_Manual.pdf')}`);
  };

  return (
    <div ref={profileDetailsRef} className="profile-details">
      <div className="profile-icon-container">
        <IonIcon className="profile-user-icon" icon={personCircle} />
      </div>
      <IonRow className="ion-justify-content-center">
        <DqText variant="h2">
          {userData?.givenName} {userData?.familyName}
        </DqText>
        <IonButton
          className="pencil-icon"
          onClick={() => setEditProfileModalOpen(true)}
          fill="clear"
          size="small"
          color="dark"
        >
          <IonIcon icon={pencil} />
        </IonButton>
      </IonRow>
      <IonRow className="ion-justify-content-center">
        <DqText variant="body">{userData?.email}</DqText>
      </IonRow>
      <div className="ion-margin-top">
        <IonItem lines="full">
          <DqText slot="start" variant="body">
            {formatMessage(ProfileMessages.organization)}
          </DqText>
          <DqText slot="end" variant="body">
            {organization?.orgName ?? formatMessage(GenericMessages.unknownPlaceholder)}
          </DqText>
        </IonItem>
        <IonItem lines="full">
          <DqText variant="body">{formatMessage(ProfileMessages.role)}</DqText>
          <DqText slot="end" variant="body">
            {Roles.getPrettyPrint(userData?.role)}
          </DqText>
        </IonItem>
        <div className="ion-margin-top">
          <DqText variant="h3">{formatMessage(ProfileMessages.preferences)}</DqText>
        </div>
        <IonItem lines="full">
          <DqText variant="body">{formatMessage(ProfileMessages.darkMode)}</DqText>
          <IonToggle
            checked={theme === MODE.DARK}
            onIonChange={e => toggleDarkMode(e.detail.checked)}
            className="profile-details-toggle"
            slot="end"
            color="primary"
          />
        </IonItem>
        <IonItem lines="full">
          <DqText variant="body">{formatMessage(ProfileMessages.useMetricSystem)}</DqText>
          <IonToggle
            checked={isMetric}
            onIonChange={() => dispatch(profilePreferenceActions.setIsMetric(!isMetric))}
            className="profile-details-toggle"
            slot="end"
            color="primary"
          />
        </IonItem>
        <IonItem lines="full">
          <DqText variant="body">{formatMessage(ProfileMessages.language)}</DqText>
          <DqText slot="end" variant="body">
            {languageDisplay}
          </DqText>
          <IonNote color="dark" slot="helper">
            <FormattedMessage {...ProfileMessages.languageHelperText} />
          </IonNote>
        </IonItem>
        <div className="ion-margin-top">
          <DqText variant="h3">{formatMessage(ProfileMessages.about)}</DqText>
        </div>
        <IonItem href={'https://www.stoneagetools.com/privacy'} target="_blank" detail>
          <DqText variant="body">{formatMessage(ProfileMessages.privacyPolicy)}</DqText>
        </IonItem>
        <IonItem href={'https://www.stoneagetools.com/eula'} target="_blank" detail>
          <DqText variant="body">{formatMessage(ProfileMessages.licenseAgreement)}</DqText>
        </IonItem>
        <div className="ion-margin-top">
          <DqText variant="h3">{formatMessage(ProfileMessages.support)}</DqText>
        </div>
        <IonItem detail>
          <DqText variant="body">{formatMessage(ProfileMessages.helpCenter)}</DqText>
        </IonItem>
        <IonItem href={'https://stoneagetools.com/resource-center/ProductInformation'} target="_blank" detail>
          <DqText variant="body">{formatMessage(ProfileMessages.trainingResources)}</DqText>
        </IonItem>
        <IonItem onClick={() => openSentinelManual()} detail>
          <DqText variant="body">{formatMessage(ProfileMessages.sentinelManualOffline)}</DqText>
        </IonItem>
        <IonItem onClick={() => setFeedbackModalOpen(true)} detail>
          <DqText variant="body">{formatMessage(ProfileMessages.provideFeedback)}</DqText>
        </IonItem>
        {props.buildInfo && (
          <div className="ion-margin-top ion-align-items-center ion-flex">
            <DqText variant="body-small">
              v{props.buildInfo.version} r{props.buildInfo.build}
            </DqText>
          </div>
        )}
      </div>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSave={(e, canContact) => submitFeedback(e, canContact)}
      />
      <EditProfileModal
        isOpen={editProfilekModalOpen}
        onClose={() => cancelUpdate()}
        onSave={updatedName => updateProfile(userData?.id, updatedName)}
        userData={userData ?? {}}
      />
    </div>
  );
};
